import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { BaseLoadingScreen } from './base_loading_screen';
import { ListLoaderDelegate, ListLoader, delegateActions } from '../components/list_loader';
import { countriesApi, CountryData } from '../api/countries';
import { Country } from '../models/country';
import { session } from '../session';
import { canCreateCountry, canViewChangelog, reactListTablesEnabled } from '../permissions';
import { Action } from '../components/basic_widgets';
import { addChangelogAction, addChangelogListAction } from './changelog';
import { ListHeaderAction } from '../components/list_header';
import { FilterDelegate } from '../components/list_filters';
import * as namesApi from '../api/names';
import { NameI18nData } from '../api/names';
import { deflateList } from '../api/serialization';
import { asArray, updateLocationWithQueryString } from '../utils';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import { GridView } from '../utils/grid_view';
import { GridChoices, globalRegionsLookup } from '../utils/grid_choices';
import {
  CountriesListChoices,
  CountriesListPage,
  CountriesListProps,
  CountriesListQuery,
  CountriesListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/countries.html').default;

interface CountriesFilters {
  name_prefix?: string;
  global_region_ids?: string | string[];
}

class CountriesScreen extends BaseLoadingScreen implements ListLoaderDelegate<CountryData, Country> {
  loader: ListLoader<CountryData, Country>;
  showRegions = session.isS2bim();
  globalRegionsEnabled = !!session.tenant()?.global_regions_enabled;
  // Same rule as the legacy user-log columns.
  showUserLog = canViewChangelog(null);
  canCreate = canCreateCountry();
  listActions = ko.observableArray<ListHeaderAction>([]);

  newCountriesTable = reactListTablesEnabled();

  searchPlaceholder = i18n.t('Search countries by name')();
  nameFilter = ko.observable('').extend({ throttle: 300 });

  private globalRegionFilter = ko.observableArray<NameI18nData>(null);
  newFilters: FilterDelegate[] = this.globalRegionsEnabled
    ? [
        {
          title: i18n.t('Global region')(),
          entities: this.globalRegionFilter,
          list: (params: ListRequestParams) => namesApi.list('global_regions', params),
        },
      ]
    : [];

  private view = new GridView(
    'countries',
    new GridChoices<CountriesListChoices>(
      {
        global_region: globalRegionsLookup,
      },
      () => ({
        globalRegion: [],
      })
    )
  );

  private subscriptions: KnockoutSubscription[] = [];

  tableProps = ko.pureComputed<CountriesListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    showRegions: this.showRegions,
    globalRegionsEnabled: this.globalRegionsEnabled,
    showUserLog: this.showUserLog,
  }));

  constructor(params: { filters: CountriesFilters }) {
    super();

    if (this.canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/countries/new/',
        tooltipTitle: i18n.t('Add country')(),
      });
    }
    this.listActions.push(...addChangelogListAction('country'));

    this.loadedAfter(
      this.newCountriesTable ? this.view.load(params.filters) : this.loadLegacyFilters(params.filters)
    );
  }

  /** The legacy filter bar shows names; the query string carries ids. */
  private loadLegacyFilters(urlParams: CountriesFilters): Promise<void> {
    if (urlParams.name_prefix) {
      this.nameFilter(urlParams.name_prefix);
    }

    let globalRegionIds = asArray(urlParams.global_region_ids);
    if (globalRegionIds.length === 0 || !this.globalRegionsEnabled) {
      return Promise.resolve();
    }

    return namesApi.list('global_regions', { ids: globalRegionIds }).then((globalRegions) => {
      if (globalRegions) {
        this.globalRegionFilter(globalRegions);
      }
    });
  }

  onReady(loader: ListLoader<CountryData, Country>) {
    this.loader = loader;
    this.subscriptions.push(this.nameFilter.subscribe(() => this.loader.forceLoad()));
  }

  fetch(params: ListRequestParams) {
    let filters = this.getFilters();
    updateLocationWithQueryString(filters);
    return countriesApi.list({ ...filters, ...params });
  }

  private getFilters() {
    return {
      name_prefix: this.nameFilter(),
      global_region_ids: deflateList(this.globalRegionFilter),
    };
  }

  private fetchPage = (query: CountriesListQuery): Promise<CountriesListPage> =>
    this.view.fetchListPage(query, countriesApi.list, 'name_prefix', this);

  toRow = (country: Country): CountriesListRow => ({
    id: country.id(),
    name: country.name(),
    url: session.toTenantPath(country.editUrl()),
    regionNames: country.regionNames(),
    globalRegionName: translate(country.globalRegion()?.name_json),
    code: country.isoCountryCode(),
    createdByName: country.data?.created_by_user_name || '',
    modifiedByName: country.data?.modified_by_user_name || '',
    actions: delegateActions(this, country, this.view.reload),
  });

  instantiate = (data: CountryData) => new Country(data);

  getEditUrl(entity: Country) {
    return entity.editUrl();
  }

  remove(id: string) {
    return countriesApi.remove(id);
  }

  canRemove(entity: Country) {
    return entity.canDelete();
  }

  getActions(entity: Country): Action[] {
    return addChangelogAction('country', entity.id());
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }
}

export let countries = {
  name: 'countries',
  viewModel: CountriesScreen,
  template: template,
};

ko.components.register(countries.name, countries);
